import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, FormHelperText, Link, Stack, Tooltip } from '@mui/material'

import { PhoneEntryBox } from '~components/common'
import { Button, Dropdown, InputLabelStyled, TextField, Typography } from '~components/internal'
import { TwilioAccountSidCode } from '~pages/AISettingsScreen/components/TwilioAccountSection'
import { type TwilioAccountConfig } from '~types'

import GreetingField from './GreetingField'

const TWILIO_ACCOUNT_TOOLTIP =
  'To enable outbound SMS communications, add a Twilio sub-account on the ' +
  'Integrations page and attach it to this phone line.'

export interface PhoneSettingsStepProps {
  number: string
  onNumberChange: (number: string, isValid: boolean) => void
  name: string
  onNameChange: (name: string) => void
  twilioAccountConfigs: TwilioAccountConfig[]
  twilioAccountConfigId: string
  onTwilioAccountConfigIdChange: (twilioAccountConfigId: string) => void
  twilioAccountError: string | null
  defaultTransferLine: string
  onDefaultTransferLineChange: (number: string, isValid: boolean) => void
  defaultGreetingMessage: string
  onDefaultGreetingMessageChange: (message: string) => void
  voicemailProcess: { name: string; url: string } | null
  isCreatingVoicemailProcess: boolean
  onCreateVoicemailProcess: () => void
}

const PhoneSettingsStep: React.FC<PhoneSettingsStepProps> = ({
  number,
  onNumberChange,
  name,
  onNameChange,
  twilioAccountConfigs,
  twilioAccountConfigId,
  onTwilioAccountConfigIdChange,
  twilioAccountError,
  defaultTransferLine,
  onDefaultTransferLineChange,
  defaultGreetingMessage,
  onDefaultGreetingMessageChange,
  voicemailProcess,
  isCreatingVoicemailProcess,
  onCreateVoicemailProcess,
}) => {
  return (
    <Stack spacing={2}>
      <Box className='phone-line-dialog__field-row'>
        <Box sx={{ flex: '0 0 auto' }}>
          <InputLabelStyled shrink className='phone-line-dialog__label'>
            Phone Number
          </InputLabelStyled>
          <PhoneEntryBox inputPhoneState={number} includeExt={false} onChange={onNumberChange} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextField
            label='Name'
            placeholder='Enter a name for the phone line'
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
          />
        </Box>
      </Box>

      <Box>
        <InputLabelStyled shrink className='phone-line-dialog__label'>
          Default Transfer Line
        </InputLabelStyled>
        <PhoneEntryBox
          inputPhoneState={defaultTransferLine}
          onChange={onDefaultTransferLineChange}
        />
      </Box>

      <GreetingField
        label='Default greeting message'
        id='default-greeting'
        value={defaultGreetingMessage}
        onChange={onDefaultGreetingMessageChange}
        placeholder='Enter the default greeting message...'
      />

      <Tooltip title={TWILIO_ACCOUNT_TOOLTIP} placement='top-start'>
        <Box>
          <Dropdown
            label='Twilio Account'
            placeholder='Polimorphic (default)'
            helperText={
              twilioAccountConfigs.length > 0
                ? 'The Twilio account that owns this phone number'
                : 'No Twilio sub-accounts are set up. Add one from the Twilio card on the Integrations page.'
            }
            error={!!twilioAccountError}
            errorText={twilioAccountError ?? undefined}
            disabled={twilioAccountConfigs.length === 0}
            fullWidth
            allowDeselect
            value={twilioAccountConfigId}
            onChange={(value) => onTwilioAccountConfigIdChange(value as string)}
            options={twilioAccountConfigs.map((config) => ({
              value: config.id,
              label: config.name,
              chip: (
                <Box className='phone-line-dialog__twilio-account-option'>
                  <Typography variant='body2'>{config.name}</Typography>
                  <TwilioAccountSidCode sid={config.account_sid} />
                </Box>
              ),
            }))}
          />
        </Box>
      </Tooltip>

      <Box>
        <InputLabelStyled shrink className='phone-line-dialog__label'>
          Voicemail Intake
        </InputLabelStyled>
        {voicemailProcess ? (
          <Link component={RouterLink} to={voicemailProcess.url} target='_blank'>
            {voicemailProcess.name}
          </Link>
        ) : (
          <Button
            disabled={isCreatingVoicemailProcess}
            onClick={onCreateVoicemailProcess}
            variant='outlined'
            size='small'
          >
            Create Voicemail Intake
          </Button>
        )}
        <FormHelperText>
          Fallback for transferred calls if no default transfer line is set.
        </FormHelperText>
      </Box>
    </Stack>
  )
}

export default PhoneSettingsStep
